import { ApplicationView, useController } from '@aha-app/mvc';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import lineSVG from '../assets/icons/line.svg';
import lineBezierSVG from '../assets/icons/lineBezier.svg';
import lineOrthogonalSVG from '../assets/icons/lineOrthogonal.svg';
import WhiteboardController from '../controllers/WhiteboardController';
import { ConnectorRouting } from '../objects/ConnectorRouting';
import styles from '../styles.module.css';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import { svgToIcon } from '../utils/svg';
import Tool, { type ToolButtonComponent } from './Tool';

function iconForRouting(routing) {
  let iconFile;
  switch (routing) {
    case ConnectorRouting.ROUTING_STRAIGHT:
      iconFile = lineSVG;
      break;
    case ConnectorRouting.ROUTING_BEZIER:
      iconFile = lineBezierSVG;
      break;
    case ConnectorRouting.ROUTING_ORTHOGONAL_AUTO:
    case ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL:
      iconFile = lineOrthogonalSVG;
      break;
    default:
      throw new Error(`Unknown routing ${routing}`);
  }

  return svgToIcon(iconFile);
}

const Popup: React.FC<{
  currentLineRouting: number;
  setCurrentLineRouting: (routing: number) => void;
  selection: fabric.Object;
}> = ApplicationView(
  ({ currentLineRouting, setCurrentLineRouting, selection }) => {
    const controller = useController(WhiteboardController);

    const setLineRouting = routing => {
      setCurrentLineRouting(routing);
      selection.setOnObjects('lineRouting', routing);
      Tool.toolActionComplete({ controller });
    };

    return (
      <ToolbarMenu columns={3}>
        <ToolButton
          className={classNames({
            [styles.toolButtonActive]:
              currentLineRouting === ConnectorRouting.ROUTING_STRAIGHT,
          })}
          onClick={() => setLineRouting(ConnectorRouting.ROUTING_STRAIGHT)}
        >
          {iconForRouting(ConnectorRouting.ROUTING_STRAIGHT)}
        </ToolButton>
        <ToolButton
          className={classNames({
            [styles.toolButtonActive]:
              currentLineRouting === ConnectorRouting.ROUTING_BEZIER,
          })}
          onClick={() => setLineRouting(ConnectorRouting.ROUTING_BEZIER)}
        >
          {iconForRouting(ConnectorRouting.ROUTING_BEZIER)}
        </ToolButton>
        <ToolButton
          className={classNames({
            [styles.toolButtonActive]:
              currentLineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_AUTO ||
              currentLineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL,
          })}
          onClick={() =>
            setLineRouting(ConnectorRouting.ROUTING_ORTHOGONAL_AUTO)
          }
        >
          {iconForRouting(ConnectorRouting.ROUTING_ORTHOGONAL_AUTO)}
        </ToolButton>
      </ToolbarMenu>
    );
  }
);

const ConnectorTypeButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    const lineRouting = selection.firstObject().get('lineRouting');
    const [currentLineRouting, setCurrentLineRouting] = useState(lineRouting);

    // Update color if the props change.
    useEffect(() => {
      setCurrentLineRouting(lineRouting);
    }, [lineRouting]);

    return (
      <ToolButton
        popup={
          <Popup
            currentLineRouting={currentLineRouting}
            setCurrentLineRouting={setCurrentLineRouting}
            selection={selection}
          />
        }
        id='connectorType'
        tool={tool}
      >
        {iconForRouting(currentLineRouting)}
      </ToolButton>
    );
  }
);

class ToolConnectorType extends Tool {
  static toolName = 'Connector type';

  static buttonComponent() {
    return ConnectorTypeButton;
  }
}

export default ToolConnectorType;
